"use client";
import Link from "next/link";
import React from "react";
import { useRouter } from "next/navigation";

interface ViewAllButtonProps {
  href?: string;
  onClick?: () => void;
  publicAccess?: boolean; // 인증 없이 접근 허용 (예: /search)
  variant?: "text" | "arrows"; // "text": 전체보기 텍스트, "arrows": 좌우 화살표 버튼
  // arrows variant 전용 props
  onPrevious?: () => void; // 왼쪽 화살표 클릭 시
  onNext?: () => void; // 오른쪽 화살표 클릭 시
  canPrevious?: boolean; // 이전 페이지 가능 여부
  canNext?: boolean; // 다음 페이지 가능 여부
}

export function ViewAllButton({ href, onClick, publicAccess, variant = "text", onPrevious, onNext, canPrevious, canNext }: ViewAllButtonProps) {
  const router = useRouter();

  const handleClick: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = async (e) => {
    // 로그인 상태와 관계없이 바로 실행
    onClick?.();
    if (href) {
      router.push(href);
    }
  };

  // arrows variant에서 undefined 처리
  const actualCanPrevious = canPrevious ?? false;
  const actualCanNext = canNext ?? false;
  
  
  const content = variant === "arrows" ? (
    <div className="inline-flex justify-start items-center gap-2 lg:gap-4">
      <button 
        type="button" 
        onClick={() => {
          if (actualCanPrevious && onPrevious) {
            onPrevious();
          }
        }} 
        disabled={!actualCanPrevious}
        aria-label="이전 페이지"
        className={`relative bg-transparent border-0 p-0 transition-opacity ${
          actualCanPrevious ? 'hover:opacity-70 cursor-pointer' : 'opacity-40 cursor-not-allowed'
        }`}
      >
        <svg className="w-8 h-8 lg:w-11 lg:h-11" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="22" cy="22" r="21.5" transform="rotate(90 22 22)" fill="white" stroke="#E4E5E9"/>
          <path d="M24 16L18 22L24 28" stroke="#010103" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </button>
      <button 
        type="button" 
        onClick={() => {
          if (actualCanNext && onNext) {
            onNext();
          }
        }} 
        disabled={!actualCanNext}
        aria-label="다음 페이지"
        className={`relative bg-transparent border-0 p-0 transition-opacity ${
          actualCanNext ? 'hover:opacity-70 cursor-pointer' : 'opacity-40 cursor-not-allowed'
        }`}
      >
        <svg className="w-8 h-8 lg:w-11 lg:h-11" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="22" cy="22" r="21.5" transform="matrix(4.37114e-08 1 1 -4.37114e-08 0 0)" fill="white" stroke="#E4E5E9"/>
          <path d="M20 16L26 22L20 28" stroke="#010103" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  ) : (
    <span className="box-border content-stretch inline-flex gap-1.5 lg:gap-2.5 items-center justify-start pl-0 pr-2 lg:pr-[15px] py-0 relative shrink-0 hover:opacity-70 transition-opacity cursor-pointer">
      <span className="font-['Pretendard:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#010103] text-sm lg:text-[18px] text-nowrap">
        <span className="leading-[1.4] whitespace-pre">전체 보기</span>
      </span>
      <span className="flex h-2.5 lg:h-[12px] items-center justify-center relative shrink-0 w-1.5 lg:w-[6px]">
        <span className="flex-none rotate-[90deg] scale-y-[-100%]">
          <span className="h-1 lg:h-1.5 relative w-2 lg:w-3 inline-block">
            <span className="absolute inset-[-11.67%_-5.83%_-16.5%_-5.83%]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 14 8" aria-hidden="true">
                <path d="M1 1L7 7L13 1" stroke="var(--stroke-0, #010103)" strokeLinecap="round" strokeWidth="1.4" />
              </svg>
            </span>
          </span>
        </span>
      </span>
    </span>
  );

  // arrows variant는 개별 버튼으로 처리되므로 여기서는 text variant만 처리
  if (variant === "arrows") {
    return content as React.ReactElement;
  }

  const ariaLabel = "전체 보기";

  if (href) {
    return (
      <Link href={href} aria-label={ariaLabel} onClick={handleClick}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" aria-label={ariaLabel} onClick={handleClick} className="bg-transparent border-0 p-0">
      {content}
    </button>
  );
}

